import { useRef, type MutableRefObject } from 'react'

/**
 * "Latest-value" ref — expose the most recent render's value to stable
 * closures (polling tick bodies, event handlers captured in useCallback
 * without deps, timer callbacks).
 *
 * This wraps the standard idiom:
 *
 *   const ref = useRef(value)
 *   ref.current = value   // during render
 *
 * `react-hooks/refs` flags the during-render assignment because refs
 * written during render aren't rolled back if React aborts the render
 * (concurrent mode). For this idiom that's fine — the purpose IS to
 * reflect the latest rendered value, and if the render aborts, the
 * component hasn't committed anyway, so no effect body runs against
 * the aborted value.
 *
 * Moving the write to `useEffect` introduces a one-render lag where
 * closures read the prior value — for the Sales Nav poller, that
 * means a tick could read a stale cursor right after a pagination
 * advance. The during-render write avoids that.
 *
 * One disable lives here, in one place, with this explanation. Every
 * caller gets to use the hook without its own disable comment.
 */
export function useLatestRef<T>(value: T): MutableRefObject<T> {
  const ref = useRef(value)
  // eslint-disable-next-line react-hooks/refs
  ref.current = value
  return ref
}
